import {
  deliveryApi,
  isActiveDispatchStatus,
  type DeliveryDispatchStatus,
  type DeliveryPaymentMode,
  type DeliveryProviderCapability,
  type DeliveryProviderType,
  type DeliveryQuote,
} from './delivery-providers-client';
import { ApiError } from './api-error';
import { appendBrandIds } from './brand-filter-params';

/**
 * Dispatches (spec §3 `delivery_dispatch`, §4.2, §4.3): one rider booking, either for an order
 * (`kind: 'order'`) or on its own — a "Solo envío" with no order row (`kind: 'standalone'`).
 */
export type DeliveryDispatchKind = 'order' | 'standalone';

/** Where the rider goes. `raw` is the line the rider reads; the coordinates are what Picker routes on. */
export interface DeliveryAddressDto {
  raw: string;
  /** A picked saved address's resolved form — what the rider reads; `raw` stays that address's own text. */
  resolvedAddress?: string;
  lat: number;
  lng: number;
  apartment?: string;
  city?: string;
  zipCode?: string;
  instructions?: string;
}

/** One entry of the append-only `events` log. */
export interface DeliveryDispatchEvent {
  at: string;
  status: DeliveryDispatchStatus;
  providerStatusText?: string | null;
  source?: 'webhook' | 'read' | 'local';
}

/**
 * A dispatch as the API returns it. Money columns are `numeric` on the backend and may arrive as
 * strings — read them through {@link amountOf}.
 */
export interface DeliveryDispatchDto {
  id: string;
  brandId: string;
  locationId: string;
  /** Sent by list reads so a card can name its branch; optional on purpose. */
  locationName?: string | null;
  providerId?: string;
  provider?: DeliveryProviderType;
  bindingId?: string;
  orderId: string | null;
  kind: DeliveryDispatchKind;
  status: DeliveryDispatchStatus;
  providerStatusText: string | null;
  providerStatusCode?: number | null;
  externalBookingId?: string | null;
  bookingNumericId: string | null;
  customerName: string;
  customerPhone: string;
  customerEmail?: string | null;
  address: DeliveryAddressDto;
  orderAmount: number | string;
  deliveryFee: number | string;
  providerFee: number | string | null;
  currency?: string | null;
  paymentMode: DeliveryPaymentMode;
  prepTimeMinutes: number;
  /**
   * When a booking Menteo holds LOCALLY goes out to the provider (a provider without `HOLD`: the prep
   * time less its courier lead). Null for a provider that holds itself (Picker) or a booking sent at
   * once; absent on an older API — read through `localHoldUntil`.
   */
  bookAfter?: string | null;
  riderInstructions?: string | null;
  trackingUrl: string | null;
  merchantUrl: string | null;
  driverName: string | null;
  driverPhone: string | null;
  driverPhotoUrl: string | null;
  proofOfDeliveryUrl: string | null;
  /**
   * The hand-over code, when the vendor gives one. Only a vendor with `DELIVERY_CODE` (Logística 21:
   * 7 digits the customer tells the courier) means it for the operator — Picker's is never shown.
   */
  validationCode?: string | null;
  cancelReason: string | null;
  cancelledBy?: 'business' | 'provider' | 'admin' | null;
  errorMessage: string | null;
  /** The backend's own reading of "an operator has to look at this" — preferred when present. */
  needsAttention?: boolean;
  requestedBy?: string | null;
  events: DeliveryDispatchEvent[];
  requestedAt: string;
  assignedAt?: string | null;
  onTheWayAt?: string | null;
  deliveredAt?: string | null;
  /** Set when the operator dismissed a failed run (`POST /deliveries/:id/dismiss`), or the run closed. */
  closedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * `order.delivery` (spec §3): the read-only summary the dispatch module writes onto the order, like
 * `order.printing`. Feeds the board chip, the "En camino" lane and the detail panel's first paint.
 */
export interface OrderDeliverySummary {
  dispatchId: string;
  provider: DeliveryProviderType;
  status: DeliveryDispatchStatus;
  trackingUrl?: string | null;
  driverName?: string | null;
  driverPhone?: string | null;
  bookingNumericId?: string | null;
  cancelledBy?: 'business' | 'provider' | 'admin' | null;
  needsAttention?: boolean;
  updatedAt: string;
}

/**
 * One branch the session can call riders for (`GET /deliveries/capabilities`, aggregate-aware):
 * the provider-level flags the board and the order panel need, without the brand-scoped config read.
 */
export interface DeliveryCapability {
  locationId: string;
  provider: DeliveryProviderType;
  dispatchMode: 'manual' | 'auto';
  /** The provider is switched on (a paused one keeps its links but calls no riders). */
  enabled: boolean;
  myFleet: boolean;
  /** `terminal` can travel for this branch (own fleet on a provider that supports it). */
  supportsTerminal: boolean;
  /** The branch's effective prep time — what the rider dialogs start on. Absent on an older API. */
  prepTimeMinutes?: number;
  /** What the vendor can do. Absent on an older API — read through `supportsOf`, which falls back to the registry. */
  capabilities?: DeliveryProviderCapability[];
  /** Minutes before the order is ready the courier is asked for. Absent on an older API — `courierLeadOf`. */
  courierLeadMinutes?: number;
}

/** `GET /deliveries/geocode` — a typed address placed on the map. */
export interface DeliveryGeocodeResult {
  lat: number;
  lng: number;
  formatted?: string | null;
}

/**
 * Whether a run needs an operator: the backend's `needsAttention` when it sends one, else no rider,
 * not delivered, never created — or cancelled by anyone but the business itself (a cancellation the
 * operator made is an outcome, not a problem).
 */
export function dispatchNeedsAttention(
  dispatch:
    { status: DeliveryDispatchStatus; cancelledBy?: string | null; needsAttention?: boolean } | null | undefined,
): boolean {
  if (!dispatch) return false;
  if (typeof dispatch.needsAttention === 'boolean') return dispatch.needsAttention;
  if (dispatch.status === 'no_courier' || dispatch.status === 'failed' || dispatch.status === 'error') return true;
  return dispatch.status === 'cancelled' && dispatch.cancelledBy !== 'business';
}

/** `POST /orders/:id/delivery/request` body (spec §4.2). */
export interface RequestOrderDeliveryBody {
  /** 0 = look for a rider now. */
  prepTimeMinutes?: number;
  riderInstructions?: string;
  /** Defaults server-side from the order's payment preference. */
  paymentMode?: DeliveryPaymentMode;
  /**
   * The phone the rider calls, for an order whose customer has none usable (empty, or an Instagram
   * `ig:` id). Without it such an order answers 409 `DELIVERY_CUSTOMER_PHONE_REQUIRED`.
   */
  customerPhone?: string;
}

/**
 * The address a new standalone delivery goes to. A saved address carries its coordinates; a typed
 * one may not — the UI has no geocoder, and spec §4.3 has the backend geocode a typed address
 * (`GeocodingService.forwardGeocode`), so `lat`/`lng` are sent only when known.
 */
export type DeliveryAddressInput = Omit<DeliveryAddressDto, 'lat' | 'lng'> & { lat?: number; lng?: number };

/** `POST /deliveries` body (spec §4.3). */
export interface CreateStandaloneDeliveryBody {
  locationId: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  address: DeliveryAddressInput;
  orderAmount: number;
  deliveryFee: number;
  paymentMode: DeliveryPaymentMode;
  prepTimeMinutes?: number;
  riderInstructions?: string;
}

/** `GET /deliveries` filters (spec §4.3). */
export interface ListDeliveriesQuery {
  status?: DeliveryDispatchStatus[];
  locationId?: string[];
  /** The aggregate header's brand filter, as every other list read sends it (`brandIdsParam`). */
  brandIds?: string[];
  from?: string;
  to?: string;
}

/** A cancellation reason the provider accepts (Picker `GET /bookings/cancel-reasons`). */
export interface DeliveryCancelReason {
  /** What is sent back as `reason`. */
  value: string;
  label: string;
}

/**
 * Delivery reads an older API does not have yet. A 404 on these routes means the route itself is
 * missing (this UI shipped before its backend), so the feature is off for the rest of the session:
 * the queries stop retrying and refetching instead of hitting the 404 every 20 seconds.
 */
export type DeliveryFeature = 'deliveries' | 'capabilities';
const unavailableFeatures = new Set<DeliveryFeature>();

export function isDeliveryFeatureUnavailable(feature: DeliveryFeature): boolean {
  return unavailableFeatures.has(feature);
}

export function isNotFoundError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

/** React Query `retry` for these reads: never on a 404, once otherwise (the app default). */
export function retryUnlessNotFound(failureCount: number, err: unknown): boolean {
  return !isNotFoundError(err) && failureCount < 1;
}

async function guardFeature<T>(feature: DeliveryFeature, read: () => Promise<T>): Promise<T> {
  try {
    return await read();
  } catch (err) {
    if (isNotFoundError(err)) unavailableFeatures.add(feature);
    throw err;
  }
}

/** A `numeric` column as a number (TypeORM hands `numeric` back as a string). */
export function amountOf(value: number | string | null | undefined): number {
  const parsed = typeof value === 'string' ? Number(value) : (value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * The provider's reasons, whatever shape they arrive in: a list of strings, a list of objects
 * (`{ reason }`, `{ text }`, `{ label, value }`, `{ id, name }`…), or either wrapped in `{ reasons }`
 * / `{ items }`. The spec freezes the route, not Picker's payload, so this stays forgiving.
 */
export function normalizeCancelReasons(raw: unknown): DeliveryCancelReason[] {
  const list: unknown[] = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object'
      ? ((raw as { reasons?: unknown[]; items?: unknown[] }).reasons ?? (raw as { items?: unknown[] }).items ?? [])
      : [];
  const reasons: DeliveryCancelReason[] = [];
  for (const entry of list) {
    if (typeof entry === 'string' && entry.trim()) {
      reasons.push({ value: entry, label: entry });
      continue;
    }
    if (entry && typeof entry === 'object') {
      const record = entry as Record<string, unknown>;
      const label = [record.label, record.text, record.reason, record.name, record.title, record.value].find(
        (candidate): candidate is string => typeof candidate === 'string' && candidate.trim() !== '',
      );
      if (!label) continue;
      const value = [record.value, record.reason, record.text, record.label, record.name].find(
        (candidate): candidate is string => typeof candidate === 'string' && candidate.trim() !== '',
      );
      reasons.push({ value: value ?? label, label });
    }
  }
  return reasons;
}

/** `GET /deliveries` answers a list; tolerate `{ data }` / `{ items }` around it. */
function listOf<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    const record = raw as { data?: unknown; items?: unknown };
    if (Array.isArray(record.data)) return record.data as T[];
    if (Array.isArray(record.items)) return record.items as T[];
  }
  return [];
}

/**
 * An API whose `GET /deliveries` predates `brandIds` refuses it (its query DTO forbids unknown keys:
 * 400 "property brandIds should not exist"). Then the read goes out without it for the rest of the
 * session, and the caller's own brand filter on the rows (they carry `brandId`) does the narrowing.
 */
let listRejectsBrandIds = false;

function rejectsBrandIds(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 400 &&
    /brandIds should not exist/.test(JSON.stringify(err.data?.message ?? ''))
  );
}

function listParams(query: ListDeliveriesQuery): string {
  const params = new URLSearchParams();
  // Repeated plain keys, the same serialisation the board's `/orders/board` reads already use: it
  // parses as an array under both of Express's query parsers, `status[]=` only under the extended one.
  query.status?.forEach((status) => params.append('status', status));
  query.locationId?.forEach((id) => params.append('locationId', id));
  appendBrandIds(params, query.brandIds);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  const search = params.toString();
  return search ? `?${search}` : '';
}

/**
 * Whether a refused rider request (`POST /orders/:id/delivery/request`) is about the typed
 * `customerPhone`. The route's own 400 is (its contract: "customerPhone is not a valid phone") and
 * carries one sentence; a body that fails validation answers a list instead, each entry naming its
 * field — that one is the phone's only when every entry is.
 */
export function isCustomerPhoneRejection(err: unknown): err is ApiError {
  if (!(err instanceof ApiError) || err.status !== 400) return false;
  const message = err.data.message;
  if (typeof message === 'string') return true;
  return (
    Array.isArray(message) &&
    message.length > 0 &&
    message.every((entry) => typeof entry === 'string' && entry.startsWith('customerPhone'))
  );
}

export const orderDeliveryClient = {
  /**
   * The order's dispatch, or null when it never had one. `refresh` re-reads the provider first
   * ("Actualizar").
   */
  async get(orderId: string, options: { refresh?: boolean } = {}): Promise<DeliveryDispatchDto | null> {
    try {
      const dispatch = await deliveryApi<DeliveryDispatchDto | null>(
        `/orders/${orderId}/delivery${options.refresh ? '?refresh=true' : ''}`,
      );
      return dispatch ?? null;
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  /**
   * Ask for a rider (manual dispatch). Answers the dispatch in `requested`; the booking is created by
   * a background job. 409 `DELIVERY_PAYMENT_METHOD_UNSUPPORTED` for a POS-at-the-door order without
   * own fleet, `DELIVERY_PROVIDER_NOT_BOUND` when the branch has no binding,
   * `DELIVERY_CUSTOMER_PHONE_REQUIRED` when the order has no usable phone and `customerPhone` is unset.
   */
  request: (orderId: string, body: RequestOrderDeliveryBody) =>
    deliveryApi<DeliveryDispatchDto>(`/orders/${orderId}/delivery/request`, { method: 'POST', body }),

  cancel: (orderId: string, reason: string) =>
    deliveryApi<DeliveryDispatchDto>(`/orders/${orderId}/delivery/cancel`, { method: 'POST', body: { reason } }),
};

export const deliveriesClient = {
  /**
   * Standalone dispatches for the board (session-scoped like orders). Empty without a request once
   * the route is known to be missing: a query still mounted under an older key (the refresh button
   * refetches every board read) must not send the 404 again.
   */
  async list(query: ListDeliveriesQuery = {}): Promise<DeliveryDispatchDto[]> {
    if (unavailableFeatures.has('deliveries')) return [];
    return guardFeature('deliveries', async () => {
      const read = async (sent: ListDeliveriesQuery) =>
        listOf<DeliveryDispatchDto>(await deliveryApi<unknown>(`/deliveries${listParams(sent)}`));
      const brandIds = listRejectsBrandIds ? undefined : query.brandIds;
      try {
        return await read({ ...query, brandIds });
      } catch (err) {
        if (!brandIds?.length || !rejectsBrandIds(err)) throw err;
        listRejectsBrandIds = true;
        return read({ ...query, brandIds: undefined });
      }
    });
  },

  get: (id: string) => deliveryApi<DeliveryDispatchDto>(`/deliveries/${id}`),

  create: (body: CreateStandaloneDeliveryBody) =>
    deliveryApi<DeliveryDispatchDto>('/deliveries', { method: 'POST', body }),

  /** Live provider quote for the "Nuevo envío" dialog. 409 `DELIVERY_PROVIDER_NOT_BOUND` for an unlinked branch. */
  quote: (params: { locationId: string; lat: number; lng: number; paymentMode: DeliveryPaymentMode }) => {
    const search = new URLSearchParams({
      locationId: params.locationId,
      lat: String(params.lat),
      lng: String(params.lng),
      paymentMode: params.paymentMode,
    });
    return deliveryApi<DeliveryQuote>(`/deliveries/quote?${search.toString()}`);
  },

  /** The branches this session can call riders for, with their provider flags (none once the route is known missing). */
  async capabilities(): Promise<DeliveryCapability[]> {
    if (unavailableFeatures.has('capabilities')) return [];
    return guardFeature('capabilities', async () => {
      const body = await deliveryApi<{ locations?: DeliveryCapability[] } | DeliveryCapability[] | null>(
        '/deliveries/capabilities',
      );
      if (Array.isArray(body)) return body;
      return body?.locations ?? [];
    });
  },

  /** Places a typed address on the map for the live quote. */
  geocode: (params: { locationId: string; raw: string }) => {
    const search = new URLSearchParams({ locationId: params.locationId, raw: params.raw });
    return deliveryApi<DeliveryGeocodeResult>(`/deliveries/geocode?${search.toString()}`);
  },

  async cancelReasons(locationId: string): Promise<DeliveryCancelReason[]> {
    const search = new URLSearchParams({ locationId });
    return normalizeCancelReasons(await deliveryApi<unknown>(`/deliveries/cancel-reasons?${search.toString()}`));
  },

  cancel: (id: string, reason: string) =>
    deliveryApi<DeliveryDispatchDto>(`/deliveries/${id}/cancel`, { method: 'POST', body: { reason } }),

  /** Take a failed run off the attention lane (sets `closedAt`). */
  dismiss: (id: string) => deliveryApi<DeliveryDispatchDto>(`/deliveries/${id}/dismiss`, { method: 'POST' }),
};

/** A dispatch the operator can still cancel. */
export function canCancelDispatch(dispatch: Pick<DeliveryDispatchDto, 'status'> | null | undefined): boolean {
  return isActiveDispatchStatus(dispatch?.status);
}
